import React, { useState, useRef, useEffect, useCallback } from "react";
import { Container as MapDiv, NaverMap, Marker } from "react-naver-maps";
import { useFetchRun } from "@/Hooks/useFetch";
import { BASE_URL } from "@/Store/Action";
import { useLocation } from "react-router-dom";
import ClipLoader from "react-spinners/ClipLoader";
import { getBoundingBox } from "@/Helper/HelperMap";
import { useThemeClass } from "@/Hooks/useThemeClass";

// Hooks
import { useMapConfig } from "./hooks/useMapConfig";
import { useMarkerManagement } from "./hooks/useMarkerManagement";
import { useInfoWindow } from "./hooks/useInfoWindow";

// Components
import { MapMarkers } from "./components/MapMarkers";
import { MapControls } from "./components/MapControls";

// Types
import { MapGeoFilterGet200Response } from "@models/MapGeoFilterGet200Response";
import { ModelGeoCampaign } from "@models/ModelGeoCampaign";
import { Coordinate } from "../NMap";

const S3_URL = import.meta.env.VITE_S3_URL;
const defaultCenter = { lat: 37.5665, lng: 126.978 };
const defaultZoom = 12;

interface NMapRefactoredProps {
  center: Coordinate;
  zoom: number;
  className?: string;
  currentCampaign?: any;
  markerPoints?: { latitude: number; longitude: number }[];
}

export const NMapRefactored: React.FC<NMapRefactoredProps> = ({
  center,
  zoom,
  currentCampaign,
  markerPoints
}) => {
  const themeClass = useThemeClass();
  const { pathname } = useLocation();
  const mapRef = useRef<any>(null);
  const showInfoWindowRef = useRef<(marker: ModelGeoCampaign) => void>();

  // Configuration
  const config = useMapConfig();

  // State
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isMapReady, setIsMapReady] = useState(false);
  const [isFindingLocation, setIsFindingLocation] = useState(false);
  const [mapCenter, setMapCenter] = useState<{
    lat: number;
    lng: number;
  } | null>({
    lat: center?.latitude ?? defaultCenter.lat,
    lng: center?.longitude ?? defaultCenter.lng,
  });
  const [zoomLevel, setZoomLevel] = useState<number>(zoom ?? defaultZoom);
  const [userLocation, setUserLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  // API Fetch
  const { fetchData, executeFetch, loading } = useFetchRun<
    MapGeoFilterGet200Response,
    {
      body: {
        category: string;
        latitude: number;
        longitude: number;
        min_latitude: number;
        max_latitude: number;
        min_longitude: number;
        max_longitude: number;
        zoom: number;
      };
    }
  >({
    url: `${BASE_URL}/map/geo-filter`,
    method: "GET",
    headers: {
      Authorization: "",
    },
  });

  // Custom Hooks
  const { infoWindow, currentMarker, setCurrentMarker, showInfoWindow } =
    useInfoWindow({
      mapRef,
      pathname,
      S3_URL,
      themeClass,
    });

  const { clusterMarkers, detailMarkers } = useMarkerManagement({
    fetchData,
    pathname,
    zoomLevel,
    infoWindow,
  });

  // Effects
  useEffect(() => {
    // detailPage에서는 즉시 로딩 완료 처리
    if (pathname.startsWith("/detailPage") || pathname.startsWith('/detailFamtour')) {
      setIsInitialLoading(false);
      return;
    }

    if (isMapReady && !loading) {
      setIsInitialLoading(false);
    }
  }, [isMapReady, loading, pathname]);

  useEffect(() => {
    // detailPage에서는 API 호출 방지
    if (pathname.startsWith("/detailPage")) {
      return;
    }

    if (mapCenter && zoomLevel) {
      const minMax = getBoundingBox(mapCenter.lat, mapCenter.lng, zoomLevel);
      fetchCampaigns(
        mapCenter.lat,
        mapCenter.lng,
        minMax.min_latitude,
        minMax.max_latitude,
        minMax.min_longitude,
        minMax.max_longitude,
        zoomLevel
      );
    }
  }, [mapCenter, zoomLevel, pathname]);

  useEffect(() => {
    if (currentCampaign && pathname.startsWith("/detailPage")) {
      setMapCenter({
        lat: currentCampaign.latitude,
        lng: currentCampaign.longitude,
      });
      setZoomLevel(14);
    }
  }, [currentCampaign?.id, pathname]);

  // SET CENTER AND ZOOM DYNAMICALLY IF THERE ARE MULTIPLE POINTS (currently used by /detailFamtour)
  useEffect(() => {
    if (
      markerPoints &&
      markerPoints.length > 1 &&
      mapRef.current &&
      window?.naver?.maps
    ) {
      const bounds = new window.naver.maps.LatLngBounds();

      markerPoints.forEach((point) => {
        bounds.extend(
          new window.naver.maps.LatLng(point.latitude, point.longitude)
        );
      });

      mapRef.current.fitBounds(bounds);
    }
  }, [pathname, markerPoints, isMapReady]);


  // Show InfoWindow for detail page campaign
  useEffect(() => {
    if (currentCampaign && pathname.startsWith("/detailPage") && isMapReady && infoWindow) {
      const timer = setTimeout(() => {
        showInfoWindow(currentCampaign);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [currentCampaign?.id, pathname, isMapReady]); // Simplified dependencies


  // Methods
  const theme = import.meta.env.VITE_APP_THEME;
  const fetchCampaigns = useCallback(
    (
      lat: number,
      lng: number,
      min_latitude: number,
      max_latitude: number,
      min_longitude: number,
      max_longitude: number,
      zoom: number
    ) => {
      executeFetch({
        body: {
          category: theme === 'weu' ? 'weu' : 'beauty',
          latitude: lat,
          longitude: lng,
          min_latitude,
          max_latitude,
          min_longitude,
          max_longitude,
          zoom,
        },
      });
    },
    [executeFetch]
  );

  const handleMarkerClick = useCallback(
    (marker: ModelGeoCampaign &
      {originalLatitude?: ModelGeoCampaign['latitude'],
        originalLongitude?: ModelGeoCampaign['longitude']}
    ) => {
      // detailPage에서는 마커 클릭 비활성화
      if (pathname.startsWith("/detailPage")) {
        return;
      }

      if (!window?.naver?.maps) return;

      if (zoomLevel < 12) {
        const latitude = Number(marker.originalLatitude || marker.latitude);
        const longitude = Number(marker.originalLongitude || marker.longitude);

        // 클러스터 마커 클릭 시: 부드럽게 줌인하고 중심 이동
        const targetPosition = new window.naver.maps.LatLng(
          latitude,
          longitude
        );

        mapRef.current.morph(targetPosition, 17, {
          duration: 300,
          easing: "easeOutCubic",
        });

        setZoomLevel(17);
        setMapCenter({
          lat: latitude,
          lng: longitude,
        });

        setTimeout(() => {
          setCurrentMarker(marker);
          showInfoWindow(marker);
        }, 350);
      } else {
        // 일반 마커 클릭 시
        setCurrentMarker(marker);
        showInfoWindow(marker);
      }
    },
    [zoomLevel, setCurrentMarker, showInfoWindow, pathname]
  );


  useEffect(() => {
    showInfoWindowRef.current = showInfoWindow;
  }, [showInfoWindow]);

  const handleMapClick = useCallback(() => {
    if (!infoWindow || !mapRef.current) return;

    // detailPage에서는 지도 클릭 시에도 InfoWindow를 닫지 않음
    if (pathname.startsWith("/detailPage")) {
      return;
    }

    infoWindow.close();
    setCurrentMarker(null);
  }, [infoWindow, setCurrentMarker, pathname]);

  const handleFindMe = useCallback(() => {
    if (!navigator.geolocation) {
      alert("Your browser does not support geolocation.");
      return;
    }

    if (!window?.naver?.maps) return;

    setIsFindingLocation(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        const targetPosition = new window.naver.maps.LatLng(
          coords.lat,
          coords.lng
        );

        if (zoomLevel < 15) {
          mapRef.current?.morph(targetPosition, 15, {
            duration: 500,
            easing: "easeOutCubic",
          });
          setZoomLevel(15);
        } else {
          mapRef.current?.panTo(targetPosition, {
            duration: 400,
            easing: "easeOutCubic",
          });
        }

        setMapCenter(coords);
        setUserLocation(coords);

        setTimeout(() => {
          setIsFindingLocation(false);
        }, 500);
      },
      () => {
        alert("Unable to retrieve your location.");
        setIsFindingLocation(false);
      },
      { enableHighAccuracy: true }
    );
  }, [zoomLevel]);

  const handleMapRef = (ref: any) => {
    if (ref) {
      mapRef.current = ref;

      // 터치 관성 스크롤 활성화
      if (ref.setOptions) {
        ref.setOptions({
          draggable: true,
          pinchZoom: true,
          scrollWheel: true,
          keyboardShortcuts: true,
          disableKineticPan: false,
          tileTransition: true,
          zoomControl: false,
          mapTypeControl: false,
          scaleControl: false,
          logoControl: false,
          mapDataControl: false,
        });
      }

      // Delay to ensure map is fully initialized
      setTimeout(() => {
        if (window?.naver?.maps?.Event) {
          window.naver.maps.Event.addListener(ref, "zoom_changed", () => {
            const newZoom = ref.getZoom();
            setZoomLevel(newZoom);
            const center = ref.getCenter();
            setMapCenter({
              lat: center.lat(),
              lng: center.lng(),
            });
          });

          window.naver.maps.Event.addListener(ref, "dragend", () => {
            const center = ref.getCenter();
            setMapCenter({ lat: center.lat(), lng: center.lng() });
          });

          window.naver.maps.Event.addListener(ref, "click", () => {
            handleMapClick();
          });
        }

        setIsMapReady(true);
      }, 200);
    }
  };

  const createMarkerOptions = useCallback(
    (marker: ModelGeoCampaign) => {
      if (!isMapReady || zoomLevel >= 12) return null;
      if (!window?.naver?.maps?.Size || !window?.naver?.maps?.Point)
        return null;

      const div = document.createElement("div");
      div.className = `w-6 h-6 ${themeClass.bg(
        "text"
      )} rounded-full flex items-center text-center justify-center text-white text-xs shadow-md`;
      div.innerHTML = `${marker.total_campaigns}`;
      if (!div || !div.innerHTML) return null;

      return {
        icon: {
          content: div.outerHTML,
          size: new window.naver.maps.Size(40, 40),
          anchor: new window.naver.maps.Point(22, 20),
        },
      };
    },
    [isMapReady, zoomLevel, themeClass]
  );

  return (
    <div className={config.containerClassName}>
      <MapControls
        pathname={pathname}
        isFindingLocation={isFindingLocation}
        onFindMe={handleFindMe}
      />

      {isInitialLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white">
          <div className="text-gray-600 text-sm">
            <ClipLoader />
          </div>
        </div>
      )}

      <MapDiv className={config.mapClassName}>
        <NaverMap
            ref={handleMapRef}
            center={mapCenter}
            zoom={zoomLevel}
            disableKineticPan={false}
          >
            {isMapReady && (
              <>
                {pathname.startsWith("/detailFamtour") && markerPoints?.length ? (
                  markerPoints.map((point, index) => {
                    return <Marker
                      key={`custom-marker-${index}`}
                      position={{
                        lat: Number(point.latitude),
                        lng: Number(point.longitude),
                      }}
                      icon={{
                        content: `
                      <div style="
                        width: 27px;
                        height: 27px;
                        background-color: ${themeClass
                          .bg("text")
                          .replace("bg-[", "")
                          .replace("]", "")};
                        border-radius: 9999px 9999px 9999px 0;
                        transform: rotate(-45deg);
                        position: relative;
                        box-shadow: 0 1px 3px rgba(0,0,0,0.3);
                      ">
                        <div style="
                          width: 18px;
                          height: 18px;
                          background: white;
                          border-radius: 9999px;
                          position: relative;
                          top: 4px;
                          left: 4px;
                        ">
                          <p style="
                          transform: rotate(45deg) translate(20%, -10%);
                          font-size: 14px;
                          ">${index+1}</p>
                        </div>
                      </div>
                    `,
                        size: new window.naver.maps.Size(20, 20),
                        anchor: new window.naver.maps.Point(12, 10),
                      }}
                    />;
                  })
                ) : pathname.startsWith("/detailPage") && currentCampaign ? (
                  <Marker
                    key={`detail-campaign-${currentCampaign.id}`}
                    position={{
                      lat: Number(currentCampaign.latitude),
                      lng: Number(currentCampaign.longitude),
                    }}
                    icon={{
                      content: `
                      <div style="
                        width: 24px;
                        height: 24px;
                        background-color: ${themeClass
                        .bg("text")
                        .replace("bg-[", "")
                        .replace("]", "")};
                        border-radius: 9999px 9999px 9999px 0;
                        transform: rotate(-45deg);
                        position: relative;
                        box-shadow: 0 1px 3px rgba(0,0,0,0.3);
                      ">
                        <div style="
                          width: 8px;
                          height: 8px;
                          background: white;
                          border-radius: 9999px;
                          position: relative;
                          top: 8px;
                          left: 8px;
                        "></div>
                      </div>
                    `,
                      size: new window.naver.maps.Size(20, 20),
                      anchor: new window.naver.maps.Point(12, 10),
                    }}
                  />
                ) : (
                  <MapMarkers
                    clusterMarkers={clusterMarkers}
                    detailMarkers={detailMarkers}
                    zoomLevel={zoomLevel}
                    pathname={pathname}
                    onMarkerClick={handleMarkerClick}
                    createMarkerOptions={createMarkerOptions}
                  />
                )}

              </>
            )}

            {userLocation && (
              <Marker
                key="user-location"
                position={userLocation}
                icon={{
                  content:
                    '<div class="w-4 h-4 bg-green-600 rounded-full border-2 border-white shadow-lg"></div>',
                  size: new window.naver.maps.Size(20, 20),
                  anchor: new window.naver.maps.Point(10, 10),
                }}
              />
            )}
          </NaverMap>
      </MapDiv>
    </div>
  );
};
